<script>
import { useAlert } from 'dashboard/composables';
import { mapActions } from 'vuex';
import BaseModal from 'dashboard/components/dahreply/BaseModal.vue';
import { apiErrorMessage } from '../../../../api/coLiving/errors';
import { FIELD_CLASS } from '../constants';
import { isValidDate } from '../helpers';

const NAME_MAX = 120;

// Adds or edits a public holiday. The condo scope (`site_id`) is kept as it
// is on edit and is null (whole account) on create until the condo list lands.
export default {
  name: 'HolidayModal',
  components: { BaseModal },
  props: {
    holiday: {
      type: Object,
      default: null,
    },
  },
  emits: ['close'],
  data() {
    return {
      fieldClass: FIELD_CLASS,
      form: {
        date: this.holiday ? this.holiday.date : '',
        name: this.holiday ? this.holiday.name : '',
      },
      submitted: false,
      isSubmitting: false,
    };
  },
  computed: {
    isEdit() {
      return !!this.holiday;
    },
    fieldErrors() {
      const name = this.form.name.trim();
      return {
        date: !isValidDate(this.form.date),
        name: !name || name.length > NAME_MAX,
      };
    },
    isFormValid() {
      return !Object.values(this.fieldErrors).some(Boolean);
    },
  },
  methods: {
    ...mapActions('coLivingConfig', ['addHoliday', 'editHoliday']),
    async onSubmit() {
      this.submitted = true;
      if (!this.isFormValid) return;
      this.isSubmitting = true;
      const fields = { date: this.form.date, name: this.form.name.trim() };
      try {
        if (this.isEdit) {
          await this.editHoliday({ id: this.holiday.id, fields });
        } else {
          await this.addHoliday({ ...fields, site_id: null });
        }
        useAlert(this.$t('CO_LIVING.CALENDAR.HOLIDAY_SAVED'));
        this.$emit('close');
      } catch (err) {
        useAlert(
          apiErrorMessage(this, err, 'CO_LIVING.CALENDAR.HOLIDAY_FAILED')
        );
      } finally {
        this.isSubmitting = false;
      }
    },
  },
};
</script>

<template>
  <BaseModal @close="$emit('close')">
    <template #title>
      {{
        isEdit
          ? $t('CO_LIVING.CALENDAR.EDIT_HOLIDAY')
          : $t('CO_LIVING.CALENDAR.ADD_HOLIDAY')
      }}
    </template>

    <form
      class="grid grid-cols-1 gap-4"
      data-testid="co-living-holiday-form"
      @submit.prevent="onSubmit"
    >
      <div>
        <label
          for="co-living-holiday-date"
          class="block mb-1 text-sm text-slate-700 dark:text-slate-200"
        >
          {{ $t('CO_LIVING.CALENDAR.HOLIDAY_FIELDS.DATE') }}
        </label>
        <input
          id="co-living-holiday-date"
          v-model="form.date"
          type="date"
          :class="[
            fieldClass,
            submitted && fieldErrors.date ? 'border-red-500' : '',
          ]"
          data-testid="co-living-holiday-date"
        />
        <p
          v-if="submitted && fieldErrors.date"
          class="mt-1 text-xs text-red-500"
        >
          {{ $t('CO_LIVING.CALENDAR.HOLIDAY_ERRORS.DATE') }}
        </p>
      </div>

      <div>
        <label
          for="co-living-holiday-name"
          class="block mb-1 text-sm text-slate-700 dark:text-slate-200"
        >
          {{ $t('CO_LIVING.CALENDAR.HOLIDAY_FIELDS.NAME') }}
        </label>
        <input
          id="co-living-holiday-name"
          v-model="form.name"
          type="text"
          maxlength="120"
          :class="[
            fieldClass,
            submitted && fieldErrors.name ? 'border-red-500' : '',
          ]"
          data-testid="co-living-holiday-name"
        />
        <p
          v-if="submitted && fieldErrors.name"
          class="mt-1 text-xs text-red-500"
        >
          {{ $t('CO_LIVING.CALENDAR.HOLIDAY_ERRORS.NAME') }}
        </p>
      </div>

      <div class="flex justify-end gap-3">
        <woot-button variant="clear" type="button" @click="$emit('close')">
          {{ $t('CO_LIVING.COMMON.CANCEL') }}
        </woot-button>
        <woot-button
          type="submit"
          :is-loading="isSubmitting"
          data-testid="co-living-holiday-save"
        >
          {{ $t('CO_LIVING.COMMON.SAVE') }}
        </woot-button>
      </div>
    </form>
  </BaseModal>
</template>
